<script setup>
import { ref, watch, onMounted } from 'vue'
import Button from 'primevue/button'
import ButtonGroup from 'primevue/buttongroup'
import AutoComplete from 'primevue/autocomplete'
import Select from 'primevue/select'
import customerApi from '@/api/customerApi.js'
import { useFacilityStore } from '@/stores/facility.js'

const props = defineProps({
  facilityId: { type: [Number, String], required: false }, // режим редактирования
  modelValue: { type: Array, default: () => [] },          // [{ customer_id, role }]
  presetCustomerId: { type: [Number, String], required: false }
})
const emit = defineEmits(['update:modelValue'])

const ROLE_LABELS = {
  CUSTOMER: 'Заказчик',
  GENERAL_CONTRACTOR: 'Генеральный Подрядчик',
  CONTRACTOR: 'Подрядчик',
}
const ROLE_OPTIONS = [
  { label: ROLE_LABELS.CUSTOMER, value: 'CUSTOMER' },
  { label: ROLE_LABELS.GENERAL_CONTRACTOR, value: 'GENERAL_CONTRACTOR' },
  { label: ROLE_LABELS.CONTRACTOR, value: 'CONTRACTOR' }
]

const store = useFacilityStore()
const rows = ref([])
const suggestions = ref([])
let searchTimer = null

async function searchCustomers(q) {
  try {
    const res = await customerApi.getCustomers({ q, page: 1 })
    suggestions.value = Array.isArray(res?.results) ? res.results : []
  } catch {
    suggestions.value = []
  }
}

onMounted(async () => {
  if (props.facilityId) {
    const items = await store.loadFacilityParticipants(props.facilityId)
    rows.value = items.map(x => ({
      customer_id: x.customer_id,
      role: x.role,
      _customerObj: { id: x.customer_id, short_name: x.short_name }
    }))
  } else if (props.presetCustomerId) {
    try {
      const presetCustomer = await customerApi.getCustomer(props.presetCustomerId)
      rows.value = [{
        customer_id: Number(props.presetCustomerId),
        role: 'CUSTOMER',
        _customerObj: presetCustomer
      }]
    } catch {
      rows.value = [{
        customer_id: Number(props.presetCustomerId),
        role: 'CUSTOMER',
        _customerObj: null
      }]
    }
  } else {
    rows.value = Array.isArray(props.modelValue) ? props.modelValue.map(it => ({ ...it, _customerObj: null })) : []
  }
  emit('update:modelValue', rows.value.map(({ customer_id, role }) => ({ customer_id, role })))
})

watch(rows, () => {
  const first = rows.value.findIndex(r => r.role === 'CUSTOMER')
  if (first !== -1) {
    for (let i = rows.value.length - 1; i > first; i--) {
      if (rows.value[i].role === 'CUSTOMER') rows.value.splice(i, 1)
    }
  }
  emit('update:modelValue', rows.value.map(({ customer_id, role }) => ({ customer_id, role })))
}, { deep: true })



function addRow() {
  rows.value.push({ customer_id: null, role: null, _customerObj: null })
}
function removeRow(idx) {
  rows.value.splice(idx, 1)
}
function onCustomerComplete(e) {
  const q = String(e?.query || '').trim()
  if (searchTimer) clearTimeout(searchTimer)
  if (!q) {
    suggestions.value = []
    return
  }
  searchTimer = setTimeout(() => searchCustomers(q), 250)
}
function onCustomerSelect(e, idx) {
  const obj = e?.value || null
  rows.value[idx]._customerObj = obj
  rows.value[idx].customer_id = obj?.id ?? null
}
</script>
<template>
  <div class="flex flex-col gap-2">
    <div
        v-for="(row, idx) in rows"
        :key="idx"
        class="flex items-center gap-2"
    >
      <!-- AutoComplete: 2/3 ширины -->
      <div class="flex-[2_1_0%] min-w-0">
        <AutoComplete
            v-model="row._customerObj"
            :suggestions="suggestions"
            optionLabel="short_name"
            placeholder="Выберите организацию"
            @complete="onCustomerComplete"
            @item-select="(e) => onCustomerSelect(e, idx)"
            forceSelection="false"
            size="small"
        />
      </div>

      <!-- Select: 1/3 ширины -->
      <div class="flex-[1_1_0%] min-w-0">
        <Select
            v-model="row.role"
            :options="ROLE_OPTIONS"
            optionLabel="label"
            optionValue="value"
            placeholder="Выберите роль"
            size="small"
            class="w-full"
        />
      </div>

      <ButtonGroup>
        <Button
            icon="pi pi-plus"
            rounded text size="small"
            severity="secondary"
            @click="addRow"
            v-tooltip.top="'Добавить ещё организацию'"
        />
        <Button
            icon="pi pi-trash"
            rounded text size="small"
            severity="danger"
            @click="removeRow(idx)"
            v-tooltip.top="'Удалить организацию'"
        />
      </ButtonGroup>
    </div>

    <div v-if="rows.length === 0" class="flex">
      <Button
          label="Добавить организацию"
          icon="pi pi-plus"
          text size="small"
          @click="addRow"
      />
    </div>
  </div>
</template>

<style scoped>
/* Растянуть AutoComplete корректно:
   - корневой элемент .p-autocomplete делаем блочным flex и 100% ширины
   - инпут внутри пусть занимает всю доступную ширину */
:deep(.p-autocomplete) {
  display: flex;
  width: 100%;
}
:deep(.p-autocomplete .p-autocomplete-input) {
  width: 100%;
}

/* Подровнять высоту small у обоих контролов (на случай тем/кастомизаций) */
:deep(.p-autocomplete.p-inputwrapper-sm .p-autocomplete-input),
:deep(.p-select.p-inputwrapper-sm) {
  min-height: 2.25rem; /* ~ h-9 */
  font-size: 0.875rem; /* text-sm */
}
</style>
